import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssspreadsheet.homes';
  const articleUrl = `${siteUrl}/article/cssbuy-spreadsheet-complete-guide`;
  const articleImageUrl = 'https://readdy.ai/api/search-image?query=Overhead%20view%20of%20organized%20wooden%20desk%20with%20open%20laptop%20showing%20spreadsheet%20grid%2C%20surrounded%20by%20colorful%20fabric%20swatches%2C%20measuring%20tape%2C%20and%20product%20catalog%20printouts%2C%20creative%20workspace%20with%20mood%20board%20on%20wall%2C%20warm%20tungsten%20desk%20lamp%20glow%2C%20designer%20studio%20aesthetic&width=1920&height=820&seq=article-hero-v2&orientation=landscape';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'CSSBuy Spreadsheet Complete Guide 2026: How to Find, Compare, and Shop the Best Spreadsheet Deals from China',
    description: 'A comprehensive walkthrough on CSSBuy spreadsheets — what they are, how they work, how to find the best products, compare prices across agents like Kakobuy and Superbuy, and shop safely. Updated for 2026.',
    image: articleImageUrl,
    author: {
      '@type': 'Organization',
      name: 'CSS Spreadsheet Team',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CSSBuy Spreadsheet Hub',
      url: siteUrl,
    },
    datePublished: '2026-07-01',
    dateModified: '2026-07-06',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'en',
  };

  return (
    <main className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />

      {/* Article Hero */}
      <section className="pt-24 md:pt-28 pb-10 md:pb-16">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-600">CSSBuy Spreadsheet Complete Guide 2026</span>
            </nav>

            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
              Ultimate Guide
            </span>

            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              CSSBuy Spreadsheet Complete Guide 2026: How to Find, Compare, and Shop the Best Spreadsheet Deals from China
            </h1>

            <p className="mt-5 text-sm md:text-base text-foreground-500 leading-relaxed">
              A comprehensive walkthrough on everything you need to know about CSSBuy spreadsheets — what they are, how they work, how to find the best products, compare prices across agents like Kakobuy and Superbuy, and shop safely without getting scammed. Updated for 2026 with the latest tips and verified links.
            </p>

            {/* Author meta */}
            <div className="mt-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-800">CSS Spreadsheet Team</p>
                <p className="text-xs text-foreground-400">Updated July 2026 &middot; 12 min read</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="w-full px-4 md:px-6 lg:px-10 pb-10 md:pb-16">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl overflow-hidden aspect-[21/9] bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20organized%20wooden%20desk%20with%20open%20laptop%20showing%20spreadsheet%20grid%2C%20surrounded%20by%20colorful%20fabric%20swatches%2C%20measuring%20tape%2C%20and%20product%20catalog%20printouts%2C%20creative%20workspace%20with%20mood%20board%20on%20wall%2C%20warm%20tungsten%20desk%20lamp%20glow%2C%20designer%20studio%20aesthetic&width=1920&height=820&seq=article-hero-v2&orientation=landscape"
              alt="CSSBuy Spreadsheet workspace with fashion products"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="w-full px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-8 md:space-y-10">
            {/* Section 1 */}
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                What Is a CSSBuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                A <strong>CSSBuy spreadsheet</strong> is a curated, community-driven document that lists products available through Chinese shopping agents. Think of it as a massive catalog of verified product listings — from sneakers and hoodies to bags and accessories — each with direct purchase links, price comparisons, and quality notes. These spreadsheets are maintained by experienced shoppers who test products, verify sellers, and share their findings with the community.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Unlike browsing random stores on your own, a good CSSBuy spreadsheet saves you hours of searching and significantly reduces the risk of receiving low-quality items. Every entry typically includes the product name, agent link, estimated price in USD, size availability, and sometimes even QC (Quality Check) photos so you know exactly what you are getting before you buy.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                The term <strong>"spreadsheet"</strong> originally comes from the Google Sheets and Excel documents where early adopters compiled their finds. Today, the CSSBuy spreadsheet ecosystem has grown into a full-fledged community resource covering thousands of products across multiple shopping agents, including <strong>Kakobuy spreadsheets</strong>, <strong>Superbuy spreadsheets</strong>, and <strong>Oopbuy spreadsheets</strong>.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                How Does a CSSBuy Spreadsheet Work?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Using a CSSBuy spreadsheet is remarkably straightforward, even if you have never shopped through a Chinese agent before. Here is a step-by-step breakdown of how the process works from finding a product to receiving it at your doorstep.
              </p>
              <ol className="space-y-4 pl-5 list-decimal text-sm md:text-base text-foreground-600">
                <li className="leading-relaxed">
                  <strong>Browse the spreadsheet</strong> — Open any verified CSSBuy spreadsheet and scroll through the categories that interest you. Popular categories include Shoes, Hoodies &amp; Sweaters, T-Shirts, Jackets, Pants &amp; Shorts, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff. Each entry has a direct link to the product page on the agent's platform.
                </li>
                <li className="leading-relaxed">
                  <strong>Check product details</strong> — Before purchasing, review the product description, available sizes, color options, and any QC photos included in the listing. Many spreadsheets include community ratings and comments that help you gauge product quality.
                </li>
                <li className="leading-relaxed">
                  <strong>Compare across agents</strong> — The same product may be available through multiple agents like CSSBuy, Kakobuy, Superbuy, or Oopbuy at different prices. Spreadsheets often list multiple agent links for the same item, allowing you to choose the most affordable or fastest option.
                </li>
                <li className="leading-relaxed">
                  <strong>Place your order</strong> — Click the agent link, add the item to your cart, and complete the purchase. Most agents accept PayPal and credit cards. The agent then purchases the item from the original Chinese seller on your behalf.
                </li>
                <li className="leading-relaxed">
                  <strong>Quality check and shipping</strong> — Once the item arrives at the agent's warehouse, they provide QC photos. You can approve the item for shipping or request a return if the quality does not match expectations. Choose a shipping line based on your budget and speed preferences.
                </li>
              </ol>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                CSSBuy vs Kakobuy vs Superbuy vs Oopbuy: Which Agent Should You Use?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                Choosing the right shopping agent is crucial for a smooth spreadsheet shopping experience. Each agent has its own strengths, pricing structure, and shipping options. Here is how the four most popular spreadsheet-compatible agents compare:
              </p>

              {/* Comparison Table */}
              <div className="overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Agent</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Service Fee</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Shipping Speed</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">QC Quality</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Best For</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 font-semibold text-primary-700 whitespace-nowrap">CSSBuy</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Low (3-5%)</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Medium</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Excellent</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Budget shoppers</td>
                    </tr>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Kakobuy</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Medium (5-8%)</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Fast</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Good</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Quick delivery</td>
                    </tr>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Superbuy</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Medium (5-8%)</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Medium</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Very Good</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Reliability</td>
                    </tr>
                    <tr className="border-t border-background-200/70">
                      <td className="px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">Oopbuy</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Low (2-4%)</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Slow</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Good</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">Lowest fees</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-xs text-foreground-400">
                Note: Fees and speeds are estimates based on community feedback as of July 2026. Actual rates may vary.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Popular Product Categories on CSSBuy Spreadsheets
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                CSSBuy spreadsheets cover an extensive range of product categories. Whether you are hunting for the latest sneaker releases, cozy winter hoodies, or designer-inspired accessories, there is a spreadsheet category for every shopper. Here are the most popular categories and what you can expect to find in each:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categories.slice(0, 8).map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shopping-bag-3-line text-primary-600 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                    <i className="ri-arrow-right-up-line text-foreground-300 w-4 h-4 flex items-center justify-center ml-auto"></i>
                  </a>
                ))}
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                How to Stay Safe When Shopping with Spreadsheets
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Shopping through Chinese agents using spreadsheets is generally safe, but there are important precautions every buyer should take. The spreadsheet community has developed several best practices over the years that can save you from unpleasant surprises.
              </p>
              <ul className="space-y-3 pl-5 list-disc text-sm md:text-base text-foreground-600">
                <li className="leading-relaxed"><strong>Stick to verified spreadsheets</strong> — Only use spreadsheets that have been reviewed and endorsed by trusted community members. Avoid random links shared in unmoderated groups.</li>
                <li className="leading-relaxed"><strong>Always request QC photos</strong> — Never skip the quality check step. QC photos are your last line of defense before an item ships internationally.</li>
                <li className="leading-relaxed"><strong>Start with small orders</strong> — If you are trying a new seller or agent for the first time, place a small test order before committing to a larger purchase.</li>
                <li className="leading-relaxed"><strong>Use PayPal for buyer protection</strong> — Most agents accept PayPal, which provides an additional layer of dispute resolution if something goes wrong.</li>
                <li className="leading-relaxed"><strong>Understand customs and shipping risks</strong> — Different countries have different import regulations. Research your local customs policies before ordering restricted items.</li>
                <li className="leading-relaxed"><strong>Compare shipping lines carefully</strong> — Cheaper shipping lines may take longer but reduce the risk of customs inspection. Faster lines like DHL and FedEx are convenient but may attract more scrutiny.</li>
                <li className="leading-relaxed"><strong>Keep records of all transactions</strong> — Save screenshots of your orders, chat conversations with agents, and payment confirmations until your items arrive safely.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                FAQ: Common Questions About CSSBuy Spreadsheets
              </h2>

              <div className="space-y-5">
                <div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-2">Are CSSBuy spreadsheets free to access?</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Yes! All the spreadsheets linked from CSS Spreadsheet Hub are completely free to browse. You only pay when you decide to purchase a product through the linked agent. There is no membership fee or hidden cost to access the spreadsheet database.
                  </p>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-2">How often are the spreadsheets updated?</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Our spreadsheet links are checked and refreshed daily. New product listings are added regularly, and broken links are removed promptly. The CSSBuy spreadsheet community is highly active, with new finds shared every week.
                  </p>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-2">Can I use spreadsheets from my phone?</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    Absolutely. All CSSBuy spreadsheets and agent platforms are mobile-friendly. You can browse products, compare prices, and place orders directly from your smartphone or tablet without any issues.
                  </p>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-2">What if a product link is broken?</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    If you encounter a broken link, it likely means the product listing has been updated or removed by the seller. Try browsing other entries in the same category, or check back later — we refresh our database daily to minimize dead links.
                  </p>
                </div>
              </div>
            </section>

            {/* Internal link to homepage */}
            <section className="pt-6 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-background-100 rounded-xl p-5 md:p-6">
                <div>
                  <p className="text-sm font-semibold text-foreground-800">Ready to start browsing?</p>
                  <p className="text-xs text-foreground-500 mt-1">Explore 14 product categories with verified CSSBuy spreadsheet links.</p>
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                  <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>

              {/* CTA to shop */}
              <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-foreground-900 rounded-xl p-5 md:p-6">
                <div>
                  <p className="text-sm font-semibold text-background-50">Shop directly on CSSBuy Sheet</p>
                  <p className="text-xs text-foreground-300 mt-1">Browse thousands of products with the latest spreadsheet finds.</p>
                </div>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </section>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}